/**
 * @fileoverview Idempotency key registry.
 * @description Where the Idempotency-Keys of in-flight lifecycle commands are kept: one registry
 * per QueryClient, which outlives any dialog, so a key issued by a dialog that has since closed (or
 * unmounted mid-request) is still there when the admin reopens it and retries. Keys are scoped to
 * the logical action on a user (for example "deactivate" on EMP-2004), not to the exact request
 * body or ETag, because what must stay the same across a retry is the command the admin meant, not
 * the wording of its note. A new QueryClient (a new test, a new session) starts with no keys.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { QueryClient } from '@tanstack/react-query'

/** The lifecycle commands that send an Idempotency-Key. */
export type IdempotentAction = 'deactivate' | 'reactivate'

const registries = new WeakMap<QueryClient, Map<string, string>>()

/**
 * @description The scope a command's key is kept under: the action on one user.
 * @param {IdempotentAction} action - The lifecycle command.
 * @param {string} userId - The user it acts on.
 * @returns {string} The scope, one per action per user.
 */
export function idempotencyScope(action: IdempotentAction, userId: string): string {
  return `${action}:${userId}`
}

/**
 * @description Returns the key registry of a query client, making it on first use.
 * @param {QueryClient} queryClient - The app's (or the test's) query client.
 * @returns {Map<string, string>} The keys in use, by scope.
 */
export function idempotencyKeysOf(queryClient: QueryClient): Map<string, string> {
  let registry = registries.get(queryClient)
  if (!registry) {
    registry = new Map()
    registries.set(queryClient, registry)
  }
  return registry
}
